import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { SlidersHorizontal, X, ChevronDown, ChevronUp } from 'lucide-react';
import { filterOptions } from '../mockData';
import { FilterState } from '../types';

interface FilterBarProps {
  filters: FilterState;
  onFilterChange: (filterName: keyof FilterState, value: string | boolean) => void;
  onClearFilters: () => void;
}

const FilterBar: React.FC<FilterBarProps> = ({ filters, onFilterChange, onClearFilters }) => {
  const { t } = useTranslation();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const hasActiveFilters =
    filters.propertyType !== 'all' ||
    filters.priceRange !== 'any' ||
    filters.bedrooms !== 'any' ||
    filters.bathrooms !== 'any' ||
    filters.sqftMin !== '' ||
    filters.sqftMax !== '' ||
    filters.yearBuiltMin !== '' ||
    filters.yearBuiltMax !== '' ||
    filters.lotSize !== 'any' ||
    filters.hasHOA !== 'any' ||
    filters.hoaFeesMax !== '' ||
    filters.parkingMin !== '' ||
    filters.keywords !== '' ||
    filters.daysOnMarket !== 'any' ||
    filters.openHousesOnly ||
    filters.virtualToursOnly;

  const activeFilterCount = [
    filters.propertyType !== 'all',
    filters.priceRange !== 'any',
    filters.bedrooms !== 'any',
    filters.bathrooms !== 'any',
    filters.sqftMin !== '' || filters.sqftMax !== '',
    filters.yearBuiltMin !== '' || filters.yearBuiltMax !== '',
    filters.lotSize !== 'any',
    filters.hasHOA !== 'any',
    filters.parkingMin !== '',
    filters.keywords !== '',
    filters.daysOnMarket !== 'any',
    filters.openHousesOnly,
    filters.virtualToursOnly
  ].filter(Boolean).length;

  // Shared filter content component for reuse
  const FilterContent = ({ isMobile = false }: { isMobile?: boolean }) => (
    <div className={isMobile ? "space-y-6" : ""}>
      {/* Basic Filters - grid layout for mobile sheet */}
      <div className={isMobile ? "grid grid-cols-2 gap-4" : "flex items-center gap-2 sm:gap-3 flex-wrap"}>
        {/* Filter icon - hidden on mobile to save space */}
        {!isMobile && (
          <div className="hidden sm:flex items-center gap-2 text-gray-700 font-medium">
            <SlidersHorizontal className="h-5 w-5" />
            <span className="hidden md:inline">{t('filters.filtersLabel')}</span>
          </div>
        )}

        {/* Property Type Filter */}
        <div className={isMobile ? "col-span-1" : ""}>
          {isMobile && <Label className="text-sm font-medium text-gray-700 mb-2 block">{t('filters.type')}</Label>}
          <Select value={filters.propertyType} onValueChange={(value) => onFilterChange('propertyType', value)}>
            <SelectTrigger className={isMobile ? "w-full" : "w-[110px] sm:w-[140px] text-sm"}>
              <SelectValue placeholder={t('filters.type')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('filters.allTypes')}</SelectItem>
              {filterOptions.propertyTypes.map((type: string) => (
                <SelectItem key={type} value={type.toLowerCase()}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Price Range Filter */}
        <div className={isMobile ? "col-span-1" : ""}>
          {isMobile && <Label className="text-sm font-medium text-gray-700 mb-2 block">{t('filters.price')}</Label>}
          <Select value={filters.priceRange} onValueChange={(value) => onFilterChange('priceRange', value)}>
            <SelectTrigger className={isMobile ? "w-full" : "w-[90px] sm:w-[160px] text-sm"}>
              <SelectValue placeholder={t('filters.anyPrice')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">{t('filters.anyPrice')}</SelectItem>
              {filterOptions.priceRanges.slice(1).map((range: { label: string }, index: number) => {
                const value = range.label.toLowerCase().replace(/\s/g, '-');
                return (
                  <SelectItem key={index} value={value}>
                    {range.label}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        {/* Bedrooms Filter */}
        <div className={isMobile ? "col-span-1" : ""}>
          {isMobile && <Label className="text-sm font-medium text-gray-700 mb-2 block">{t('common.beds')}</Label>}
          <Select value={filters.bedrooms} onValueChange={(value) => onFilterChange('bedrooms', value)}>
            <SelectTrigger className={isMobile ? "w-full" : "w-[100px] sm:w-[120px] text-sm"}>
              <SelectValue placeholder={t('common.beds')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">{t('filters.anyBeds')} {t('common.beds')}</SelectItem>
              {filterOptions.bedrooms.filter((bed: string) => bed.toLowerCase() !== 'any').map((bed: string) => (
                <SelectItem key={bed} value={bed.toLowerCase()}>
                  {bed}+ {t('common.beds')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Bathrooms Filter */}
        <div className={isMobile ? "col-span-1" : ""}>
          {isMobile && <Label className="text-sm font-medium text-gray-700 mb-2 block">{t('common.baths')}</Label>}
          <Select value={filters.bathrooms} onValueChange={(value) => onFilterChange('bathrooms', value)}>
            <SelectTrigger className={isMobile ? "w-full" : "w-[100px] sm:w-[120px] text-sm"}>
              <SelectValue placeholder={t('common.baths')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">{t('filters.anyBaths')} {t('common.baths')}</SelectItem>
              {filterOptions.bathrooms.filter((bath: string) => bath.toLowerCase() !== 'any').map((bath: string) => (
                <SelectItem key={bath} value={bath.toLowerCase()}>
                  {bath}+ {t('common.baths')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* More Filters Button - only on desktop inline view */}
        {!isMobile && (
          <>
            <Button
              onClick={() => setShowAdvanced(!showAdvanced)}
              variant="outline"
              size="sm"
              className="ml-auto text-sm"
            >
              <span className="hidden sm:inline">{t('filters.moreFilters')}</span>
              <span className="sm:hidden">{t('filters.more')}</span>
              {showAdvanced ? <ChevronUp className="h-4 w-4 ml-1 sm:ml-2" /> : <ChevronDown className="h-4 w-4 ml-1 sm:ml-2" />}
            </Button>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button
                onClick={onClearFilters}
                variant="ghost"
                size="sm"
                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 text-sm px-2 sm:px-3"
              >
                <X className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">{t('filters.clearAll')}</span>
              </Button>
            )}
          </>
        )}
      </div>

      {/* Advanced Filters - always shown in mobile sheet */}
      {(showAdvanced || isMobile) && (
        <div className={isMobile ? "space-y-6 pt-4 border-t border-gray-200" : "pt-4 mt-4 border-t border-gray-200 space-y-4"}>
          {/* Square Footage */}
          <div>
            <Label className="text-sm font-medium text-gray-700 mb-2 block">{t('filters.sqft')}</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                placeholder={t('common.min')}
                value={filters.sqftMin}
                onChange={(e) => onFilterChange('sqftMin', e.target.value)}
                className="flex-1"
              />
              <span className="text-gray-500">{t('common.to')}</span>
              <Input
                type="number"
                placeholder={t('common.max')}
                value={filters.sqftMax}
                onChange={(e) => onFilterChange('sqftMax', e.target.value)}
                className="flex-1"
              />
            </div>
          </div>

          {/* Year Built */}
          <div>
            <Label className="text-sm font-medium text-gray-700 mb-2 block">{t('filters.yearBuilt')}</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                placeholder={t('common.min')}
                value={filters.yearBuiltMin}
                onChange={(e) => onFilterChange('yearBuiltMin', e.target.value)}
                className="flex-1"
              />
              <span className="text-gray-500">{t('common.to')}</span>
              <Input
                type="number"
                placeholder={t('common.max')}
                value={filters.yearBuiltMax}
                onChange={(e) => onFilterChange('yearBuiltMax', e.target.value)}
                className="flex-1"
              />
            </div>
          </div>

          {/* Additional Filters Row */}
          <div className="grid grid-cols-2 gap-4">
            {/* Lot Size */}
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">{t('filters.lotSize')}</Label>
              <Select value={filters.lotSize} onValueChange={(value) => onFilterChange('lotSize', value)}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('common.any')} />
                </SelectTrigger>
                <SelectContent>
                  {filterOptions.lotSizes.map((size: string) => (
                    <SelectItem key={size} value={size.toLowerCase()}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* HOA */}
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">{t('filters.hoa')}</Label>
              <Select value={filters.hasHOA} onValueChange={(value) => onFilterChange('hasHOA', value)}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('common.any')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">{t('common.any')}</SelectItem>
                  <SelectItem value="yes">{t('filters.hasHOA')}</SelectItem>
                  <SelectItem value="no">{t('filters.noHOA')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Parking */}
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">{t('filters.minParking')}</Label>
              <Input
                type="number"
                placeholder={t('common.any')}
                value={filters.parkingMin}
                onChange={(e) => onFilterChange('parkingMin', e.target.value)}
                min="0"
                className="w-full"
              />
            </div>

            {/* Days on Market */}
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">{t('filters.daysOnMarket')}</Label>
              <Select value={filters.daysOnMarket} onValueChange={(value) => onFilterChange('daysOnMarket', value)}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('common.any')} />
                </SelectTrigger>
                <SelectContent>
                  {filterOptions.daysOnMarket.map((days: string) => (
                    <SelectItem key={days} value={days.toLowerCase()}>
                      {days}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Keywords Search */}
          <div>
            <Label className="text-sm font-medium text-gray-700 mb-2 block">{t('filters.keywords')}</Label>
            <Input
              type="text"
              placeholder={t('filters.keywordsPlaceholder')}
              value={filters.keywords}
              onChange={(e) => onFilterChange('keywords', e.target.value)}
              className="w-full"
            />
          </div>

          {/* Checkboxes */}
          <div className="flex flex-col gap-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.openHousesOnly}
                onChange={(e) => onFilterChange('openHousesOnly', e.target.checked)}
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">{t('filters.openHousesOnly')}</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.virtualToursOnly}
                onChange={(e) => onFilterChange('virtualToursOnly', e.target.checked)}
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">{t('filters.virtualToursOnly')}</span>
            </label>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-white border-b border-gray-200 sticky top-16 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Mobile Filter Button */}
        <div className="sm:hidden flex items-center gap-2">
          <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" className="flex-1 justify-between">
                <span className="flex items-center gap-2">
                  <SlidersHorizontal className="h-4 w-4" />
                  {t('filters.filtersLabel')}
                </span>
                {activeFilterCount > 0 && (
                  <span className="bg-blue-600 text-white text-xs rounded-full px-2 py-0.5 ml-2">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="h-[85vh] overflow-y-auto pt-8">
              <SheetHeader className="mb-6">
                <SheetTitle className="text-xl font-bold">{t('filters.filtersLabel')}</SheetTitle>
              </SheetHeader>
              <FilterContent isMobile={true} />
              {/* Mobile Sheet Footer Actions */}
              <div className="sticky bottom-0 bg-white pt-4 pb-2 border-t border-gray-200 mt-6 flex gap-3">
                {hasActiveFilters && (
                  <Button
                    onClick={() => {
                      onClearFilters();
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    {t('filters.clearAll')}
                  </Button>
                )}
                <Button
                  onClick={() => setMobileFiltersOpen(false)}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  {t('filters.showResults')}
                </Button>
              </div>
            </SheetContent>
          </Sheet>

          {/* Clear Filters button on mobile */}
          {hasActiveFilters && (
            <Button
              onClick={onClearFilters}
              variant="ghost"
              size="sm"
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-2"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Desktop Filter Bar */}
        <div className="hidden sm:block">
          <FilterContent isMobile={false} />
        </div>
      </div>
    </div>
  );
};

export default FilterBar;
